import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCheck, IconPencil, IconTrash, IconX } from '@tabler/icons-react';

import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { describeSavedSearch } from './presentation';

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
const RENAME_ERROR = 'Failed to rename search. Please try again.';

interface SavedSearchesMenuProps {
  savedSearches: SavedSearchFieldsFragment[];
  activeSavedSearchId?: string | null;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  onDelete: (savedSearch: SavedSearchFieldsFragment) => void;
  onOpenFilters?: () => void;
}

export const SavedSearchesMenu = ({
  savedSearches,
  activeSavedSearchId,
  onSelect,
  onRename,
  onDelete,
  onOpenFilters,
}: SavedSearchesMenuProps) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);

  if (savedSearches.length === 0) {
    return (
      <Col className="items-start gap-3 p-3">
        <Text as="p" className="text-label-md text-od-content-primary">
          No saved searches yet
        </Text>
        <Text as="p" className="text-body-sm text-od-content-secondary">
          Set your filters and location, then tap “Save search” to pick up right where you left off
          — on any device.
        </Text>
        {onOpenFilters && (
          <Button
            analyticsName="saved-searches-empty-open-filters"
            aria-label="Set filters"
            variant="secondary"
            size="sm"
            onClick={onOpenFilters}
          >
            Set filters
          </Button>
        )}
      </Col>
    );
  }

  return (
    <Col className="py-1">
      {savedSearches.map((savedSearch) => (
        <SavedSearchRow
          key={savedSearch.id}
          savedSearch={savedSearch}
          isActive={savedSearch.id === activeSavedSearchId}
          isRenaming={renamingId === savedSearch.id}
          onStartRename={() => setRenamingId(savedSearch.id)}
          onEndRename={() => setRenamingId(null)}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
    </Col>
  );
};

interface SavedSearchRowProps {
  savedSearch: SavedSearchFieldsFragment;
  isActive: boolean;
  isRenaming: boolean;
  onStartRename: () => void;
  onEndRename: () => void;
  onSelect: (savedSearch: SavedSearchFieldsFragment) => void;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  onDelete: (savedSearch: SavedSearchFieldsFragment) => void;
}

const SavedSearchRow = ({
  savedSearch,
  isActive,
  isRenaming,
  onStartRename,
  onEndRename,
  onSelect,
  onRename,
  onDelete,
}: SavedSearchRowProps) => {
  const handleSelect = useCallback(() => {
    onSelect(savedSearch);
  }, [onSelect, savedSearch]);

  const handleDelete = useCallback(() => {
    onDelete(savedSearch);
  }, [onDelete, savedSearch]);

  if (isRenaming) {
    return <RenameRow savedSearch={savedSearch} onRename={onRename} onEndRename={onEndRename} />;
  }

  return (
    <Row className="group items-center gap-1 rounded-xl p-2 hover:bg-od-bg-hover-primary">
      <Box
        analyticsName="saved-search-select"
        aria-label={`Switch to saved search ${savedSearch.name}`}
        onClick={handleSelect}
        className="min-w-0 flex-1 flex-col items-start gap-0.5 py-1 text-left"
      >
        <Row className="w-full items-center gap-2">
          <Text as="p" className="line-clamp-1 text-body-md text-od-content-primary">
            {savedSearch.name}
          </Text>
          {isActive && (
            <Row className="shrink-0 items-center gap-0.5 rounded-full bg-od-bg-hover-primary px-2 py-0.5">
              <IconCheck size={12} aria-hidden />
              <Text as="span" className="text-body-xs text-od-content-primary">
                Active
              </Text>
            </Row>
          )}
        </Row>
        <Text as="p" className="line-clamp-1 text-body-sm text-od-content-secondary">
          {describeSavedSearch(savedSearch)}
        </Text>
      </Box>
      <Row className="shrink-0 items-center">
        <Box
          analyticsName="saved-search-rename"
          aria-label={`Rename ${savedSearch.name}`}
          onClick={onStartRename}
          className="items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
        >
          <IconPencil size={18} aria-hidden />
        </Box>
        <Box
          analyticsName="saved-search-delete"
          aria-label={`Delete ${savedSearch.name}`}
          onClick={handleDelete}
          className="items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
        >
          <IconTrash size={18} aria-hidden />
        </Box>
      </Row>
    </Row>
  );
};

interface RenameRowProps {
  savedSearch: SavedSearchFieldsFragment;
  onRename: (
    savedSearch: SavedSearchFieldsFragment,
    newName: string,
  ) => Promise<{ success: boolean; errorMessage?: string | null }>;
  onEndRename: () => void;
}

const RenameRow = ({ savedSearch, onRename, onEndRename }: RenameRowProps) => {
  const [name, setName] = useState(savedSearch.name);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleCommit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    if (trimmedName === savedSearch.name) {
      onEndRename();
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await onRename(savedSearch, trimmedName);
    setSubmitting(false);
    if (result.success) {
      onEndRename();
      return;
    }
    setErrorMessage(result.errorMessage ?? RENAME_ERROR);
  }, [name, submitting, savedSearch, onRename, onEndRename]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleCommit();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onEndRename();
      }
    },
    [handleCommit, onEndRename],
  );

  return (
    <Col className="gap-1 rounded-xl p-2">
      <Row className="items-center gap-1">
        <Input
          ref={inputRef}
          size="sm"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
          analyticsName="rename-saved-search-name-input"
          aria-label={`New name for ${savedSearch.name}`}
          disabled={submitting}
          className="min-w-0 flex-1"
        />
        <Box
          analyticsName="rename-saved-search-commit"
          aria-label="Save name"
          onClick={handleCommit}
          className={cn(
            'items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary',
            submitting && 'pointer-events-none opacity-50',
          )}
        >
          <IconCheck size={18} aria-hidden />
        </Box>
        <Box
          analyticsName="rename-saved-search-cancel"
          aria-label="Cancel rename"
          onClick={onEndRename}
          className={cn(
            'items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary',
            submitting && 'pointer-events-none opacity-50',
          )}
        >
          <IconX size={18} aria-hidden />
        </Box>
      </Row>
      {errorMessage && (
        <Text as="p" role="alert" className="text-body-sm text-od-content-error">
          {errorMessage}
        </Text>
      )}
    </Col>
  );
};
